import './AnnualLeaveTable.scss';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  FieldTimeOutlined,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { months } from '../../utils/constants';
import { AnnualPlanItem, LeaveRequest, BankHolidays, Stock } from '../../utils/types';

// Bounds the calendar's own scroll region so its header stays fixed while
// scrolling through many employees - enough vertical room for roughly 8-9
// rows before the table takes over scrolling internally.
const CALENDAR_SCROLL_Y = 560;

type AnnualLeaveTableProps = {
  annualLeaveYear: string;
  users: Stock[];
  sortOrder: string | null;
  focusedMonth?: number;
  // onDateClick: (user: User, date: Date) => void;
  selectedLeaveTypes: string[];
  bankHolidays: BankHolidays[];
  leaveRequests: LeaveRequest[];
  legendColors: Record<string, string>;
  annualPlan: AnnualPlanItem[];
};

// Still-pending statuses render at reduced opacity; finalized-positive
// (APPROVED) statuses render at full opacity - see LeaveConfig.tsx /
// TeamCalendar.tsx for where legendColors (per-type) comes from.
const PENDING_STATUSES = new Set([
  "AWAITING_MANAGER",
  "AWAITING_ADMINISTRATIV",
  "RETURNED_FOR_CORRECTION",
  "AWAITING_DOCUMENT_REVIEW",
]);
const FINAL_APPROVED_STATUSES = new Set(["APPROVED"]);

type CalendarRow = {
  key: string;
  employeeName: string;
  remainingDays: number | string;
  leaveDayInfoMap: Map<
    string,
    { type: string; comment: string; substituteName: string; status: string }
  >;
  annualPlanDates: Set<string>;
};

const AnnualLeaveTable = ({
  annualLeaveYear,
  users,
  sortOrder,
  focusedMonth,
  // onDateClick,
  selectedLeaveTypes,
  bankHolidays,
  leaveRequests,
  legendColors,
  annualPlan,
}: AnnualLeaveTableProps) => {
  const [collapsedMonths, setCollapsedMonths] = useState<
    Record<string, boolean>
  >({});

  const monthRefs = useRef<(HTMLTableCellElement | null)[]>([]);
  const currentMonthIndex = new Date().getMonth();
  const year = useMemo(() => parseInt(annualLeaveYear), [annualLeaveYear]);
  const monthsList = useMemo(() => months(year), [year]);

  const toggleMonth = useCallback((monthName: string) => {
    setCollapsedMonths((prev) => ({
      ...prev,
      [monthName]: !prev[monthName],
    }));
  }, []);

  useEffect(() => {
    if (typeof focusedMonth !== 'number') return;

    const nextCollapsedState = monthsList.reduce<Record<string, boolean>>(
      (acc, month, index) => {
        acc[month.name] = index !== focusedMonth;
        return acc;
      },
      {},
    );

    setCollapsedMonths(nextCollapsedState);
  }, [focusedMonth, monthsList]);

  const groupedArray = useMemo(() => {
    const grouped = leaveRequests.reduce(
      (acc, item) => {
        const name = item.employeeName;
        const remainingDays = item.currentYearRemainingDays;
        if (!acc[name]) {
          acc[name] = {
            employeeName: name,
            remainingDays,
            requests: [],
          };
        }
        acc[name].requests.push(item);
        return acc;
      },
      {} as Record<
        string,
        {
          employeeName: string;
          remainingDays: number;
          requests: LeaveRequest[];
        }
      >,
    );

    const groupedNames = new Set(Object.keys(grouped));

    users.forEach((user) => {
      if (!groupedNames.has(user.employeeName)) {
        grouped[user.employeeName] = {
          employeeName: user.employeeName,
          remainingDays: user.availableDays,
          requests: [
            {
              id: Math.random(),
              employeeName: user.employeeName,
              startDate: '',
              endDate: '',
              requestStatus: '',
              requestType: '',
              leaveDays: [],
              comment: '',
              substituteName: '',
              currentYearRemainingDays: user.availableDays,
            },
          ],
        };
      }
    });

    return Object.values(grouped);
  }, [leaveRequests, users]);

  const bankHolidayMap = useMemo(
    () =>
      new Map(
        bankHolidays.map((h) => [
          new Date(h.date).toLocaleDateString('sv-SE'),
          h.description,
        ]),
      ),
    [bankHolidays],
  );

  const annualPlanByEmployee = useMemo(() => {
    const map = new Map<string, Set<string>>();
    annualPlan.forEach(({ employeeName, startDate, endDate }) => {
      if (!employeeName) return;
      const dates = map.get(employeeName) ?? new Set<string>();
      for (
        let d = new Date(startDate);
        d <= new Date(endDate);
        d.setDate(d.getDate() + 1)
      ) {
        dates.add(d.toLocaleDateString('sv-SE'));
      }
      map.set(employeeName, dates);
    });
    return map;
  }, [annualPlan]);

  const buildLeaveDayInfoMap = (requests: LeaveRequest[]) => {
    const map = new Map<
      string,
      { type: string; comment: string; substituteName: string; status: string }
    >();

    requests.forEach((lq) => {
      lq.leaveDays.forEach((day) => {
        map.set(day, {
          type: lq.requestType,
          comment: lq.comment ?? '',
          substituteName: lq.substituteName ?? '',
          status: lq.requestStatus, // <-- add status here
        });
      });
    });

    return map;
  };

  const sortedGroupedArray = useMemo(() => {
    return groupedArray.slice().sort((a, b) => {
      if (a.employeeName < b.employeeName) return sortOrder === 'asc' ? -1 : 1;
      if (a.employeeName > b.employeeName) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [groupedArray, sortOrder]);

  const getCellMeta = useCallback(({
    date,
    formattedDate,
    leaveDayInfoMap,
    annualPlanDates,
  }: {
    date: Date;
    formattedDate: string;
    leaveDayInfoMap: Map<
      string,
      { type: string; comment: string; substituteName: string; status: string }
    >;
    annualPlanDates: Set<string>;
  }) => {
    const dayOfWeek = date.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

    const leaveInfo = leaveDayInfoMap.get(formattedDate);
    const status = leaveInfo?.status;
    const isPending = !!status && PENDING_STATUSES.has(status);
    const isFinalApproved = !!status && FINAL_APPROVED_STATUSES.has(status);
    const isAnnualPlanDay =
      annualPlanDates.has(formattedDate) &&
      selectedLeaveTypes.includes('ANNUAL_PLAN');

    if (isPending && !selectedLeaveTypes.includes('PENDING') && !isAnnualPlanDay) {
      return {
        className: 'month-days hidden-cell',
        popupContent: '',
        cursor: 'default',
        style: undefined,
      };
    }

    if (isFinalApproved && !selectedLeaveTypes.includes('APPROVED') && !isAnnualPlanDay) {
      return {
        className: 'month-days hidden-cell',
        popupContent: '',
        cursor: 'default',
        style: undefined,
      };
    }

    const isLeaveDay =
      !!leaveInfo?.type && selectedLeaveTypes.includes(leaveInfo.type);

    const bankHolidayDescription = bankHolidayMap.get(formattedDate);
    const isBankHoliday =
      !!bankHolidayDescription && selectedLeaveTypes.includes('BANK_HOLIDAY');

    let popupContent = '';
    if (isBankHoliday) {
      popupContent = `Holiday: ${bankHolidayDescription}`;
    } else if (isWeekend) {
      popupContent = 'Weekend';
    } else if (isPending || isFinalApproved) {
      popupContent = `${leaveInfo?.type} - ${leaveInfo?.comment || 'No comment'} | Substitute: ${leaveInfo?.substituteName || 'N/A'} | Status: ${status}`;
    } else if (isLeaveDay) {
      popupContent = `${leaveInfo.type} - ${leaveInfo.comment || 'No comment'} | Substitute: ${leaveInfo.substituteName || 'N/A'}`;
    } else if (isAnnualPlanDay) {
      popupContent = 'Annual Plan Day';
    }

    const className = `
    month-days
    ${isWeekend ? 'weekend' : ''}
    ${isAnnualPlanDay ? 'annual-plan-day' : ''}
  `;

    // Cell color comes from the per-type legend color (user-configurable, see
    // LeaveConfig.tsx), not a hardcoded status class - status is layered on
    // top as opacity so it never fights the type color for the same pixel.
    let style: { backgroundColor?: string; opacity?: number } | undefined;
    if (isBankHoliday) {
      style = { backgroundColor: legendColors.BANK_HOLIDAY };
    } else if (isLeaveDay && leaveInfo) {
      style = {
        backgroundColor: legendColors[leaveInfo.type] || '#9aa5b1',
        opacity: isPending ? 0.55 : 1,
      };
    }

    return {
      className,
      popupContent,
      cursor:
        isWeekend || isLeaveDay || isBankHoliday ? 'default' : 'pointer',
      style,
    };
  }, [selectedLeaveTypes, bankHolidayMap, legendColors]);

  const infoBtnMsg = `Calculation:
(Current year days + Previous year days) -
Used current year days - Used previous year days`;

  const columns = useMemo<ColumnsType<CalendarRow>>(() => {
    const baseColumns: ColumnsType<CalendarRow> = [
      {
        title: (
          <div className="calendar-name-container">
            <div className="calendar-name-title">Name</div>
          </div>
        ),
        dataIndex: 'employeeName',
        key: 'employeeName',
        width: 200,
        fixed: 'left',
        className: 'calendar-name',
        onHeaderCell: () => ({ className: 'calendar-name' }),
        onCell: () => ({ className: 'calendar-name' }),
      },
      {
        title: (
          <Tooltip
            title={
              <>
                <div className="remaining-days-tooltip-title">
                  Remaining annual leave days
                </div>
                <span className="info-tooltip">{infoBtnMsg}</span>
              </>
            }
          >
            <div className="remaining-days-header">
              <FieldTimeOutlined />
              <span>Remaining</span>
            </div>
          </Tooltip>
        ),
        dataIndex: 'remainingDays',
        key: 'remainingDays',
        width: 60,
        fixed: 'left',
        className: 'days-remaining-cell',
        onHeaderCell: () => ({ className: 'remaining-days-header-cell' }),
        onCell: () => ({ className: 'days-remaining-cell' }),
      },
    ];

    const monthColumns: ColumnsType<CalendarRow> = monthsList.map(
      (month, monthIndex) => {
        const isCollapsed = collapsedMonths[month.name];
        const LeadingArrow = isCollapsed ? LeftOutlined : RightOutlined;
        const TrailingArrow = isCollapsed ? RightOutlined : LeftOutlined;
        const monthTitle = (
          <div className="month-title">
            <LeadingArrow className="calendar-arrow" />
            <span>{month.name}</span>
            <TrailingArrow className="calendar-arrow" />
          </div>
        );

        if (isCollapsed) {
          return {
            title: monthTitle,
            key: `month-${month.name}`,
            onHeaderCell: () => ({
              className: 'months',
              onClick: () => toggleMonth(month.name),
            }),
            children: [
              {
                title: `1–${month.days}`,
                key: `collapsed-${month.name}`,
                width: 24,
                onHeaderCell: () => ({
                  className: 'month-days collapsed month-start month-end',
                  ref: (el: HTMLTableCellElement | null) => {
                    monthRefs.current[monthIndex] = el;
                  },
                }),
                onCell: () => ({ className: 'month-days collapsed month-start month-end' }),
                render: () => null,
              },
            ],
          };
        }

        const dayColumns = Array.from({ length: month.days }, (_, dayIndex) => {
          const date = new Date(year, monthIndex, dayIndex + 1);
          const formattedDate = date.toLocaleDateString('sv-SE');
          const dayOfWeek = date.getDay();
          const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

          return {
            title: dayIndex + 1,
            key: `${month.name}-${dayIndex + 1}`,
            dataIndex: `${month.name}-${dayIndex + 1}`,
            width: 24,
            onHeaderCell: () => ({
              className: `month-days ${isWeekend ? 'weekend' : ''} ${dayIndex === 0 ? 'month-start' : ''} ${dayIndex === month.days - 1 ? 'month-end' : ''} ${date.getDay() === 1 && dayIndex !== 0 ? 'week-divider' : ''}`,
              ref:
                dayIndex === 0
                  ? (el: HTMLTableCellElement | null) => {
                      monthRefs.current[monthIndex] = el;
                    }
                  : undefined,
            }),
            onCell: (record: CalendarRow) => {
              const cellMeta = getCellMeta({
                date,
                formattedDate,
                leaveDayInfoMap: record.leaveDayInfoMap,
                annualPlanDates: record.annualPlanDates,
              });
              return {
                className: `${cellMeta.className} ${dayIndex === 0 ? "month-start" : ""} ${dayIndex === month.days - 1 ? "month-end" : ""} ${date.getDay() === 1 && dayIndex !== 0 ? "week-divider" : ""}`,
                style: { cursor: cellMeta.cursor, ...cellMeta.style },
              };
            },
            render: (_: unknown, record: CalendarRow) => {
              const cellMeta = getCellMeta({
                date,
                formattedDate,
                leaveDayInfoMap: record.leaveDayInfoMap,
                annualPlanDates: record.annualPlanDates,
              });
              if (!cellMeta.popupContent) return null;
              return (
                <Tooltip title={cellMeta.popupContent}>
                  <span className="cell-anchor" />
                </Tooltip>
              );
            },
          };
        });

        return {
          title: monthTitle,
          key: `month-${month.name}`,
          onHeaderCell: () => ({
            className: 'months',
            onClick: () => toggleMonth(month.name),
          }),
          children: dayColumns,
        };
      },
    );

    return [...baseColumns, ...monthColumns];
  }, [
    collapsedMonths,
    getCellMeta,
    infoBtnMsg,
    monthsList,
    toggleMonth,
    year,
  ]);

  const rowData = useMemo<CalendarRow[]>(() => {
    return sortedGroupedArray.map((user) => ({
      key: user.employeeName,
      employeeName: user.employeeName,
      remainingDays: user.remainingDays,
      leaveDayInfoMap: buildLeaveDayInfoMap(user.requests),
      annualPlanDates:
        annualPlanByEmployee.get(user.employeeName) ?? new Set<string>(),
    }));
  }, [sortedGroupedArray, annualPlanByEmployee]);

  // Scroll to focused/current month once on mount/year/focusedMonth change -
  // deliberately NOT re-run on collapsedMonths. It used to be a dependency,
  // which meant every manual expand/collapse click (toggleMonth always
  // produces a new collapsedMonths object) re-triggered this and snapped the
  // view back to the focused/current month, fighting the user's own
  // scrolling - e.g. expanding December while August was focused would
  // immediately auto-scroll back to August, making December look like it
  // never rendered all 31 days.
  useEffect(() => {
    const monthIndexToFocus =
      typeof focusedMonth === 'number' ? focusedMonth : currentMonthIndex;

    const rafId = window.requestAnimationFrame(() => {
      const monthRef = monthRefs.current[monthIndexToFocus];
      const scrollContainer =
        document.querySelector('.calendar-container .ant-table-content') ||
        document.querySelector('.calendar-container');

      if (monthRef && scrollContainer) {
        // width of frozen columns: Name(200px)+DaysRemaining(60px)
        const frozenWidth = 260;

        const scrollLeft =
          monthRef.getBoundingClientRect().left -
          scrollContainer.getBoundingClientRect().left +
          scrollContainer.scrollLeft -
          frozenWidth -
          scrollContainer.clientWidth / 2 +
          100;

        scrollContainer.scrollTo({
          left: Math.max(0, scrollLeft),
          behavior: 'smooth',
        });
      }
    });

    return () => window.cancelAnimationFrame(rafId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMonthIndex, annualLeaveYear, focusedMonth]);

  return (
    <div className="al-years-table">
      <div className="calendar-container">
        <Table
          className="calendar-table"
          columns={columns}
          dataSource={rowData}
          pagination={false}
          rowClassName={() => 'calendar-user-data'}
          // x: true (not 'max-content') keeps the header/body in lockstep -
          // 'max-content' makes antd measure each cell's own content to size
          // columns, which drifts the header (e.g. a collapsed month's wide
          // "1-31" label) out of sync with the narrower 24px day cells in
          // the body as more columns render. With explicit width on every
          // leaf column, x: true just sums them and enables horizontal
          // scroll without re-measuring anything.
          //
          // y bounds the table to its own internal scroll region (same
          // fixedTableScrollY pattern used by every other table in the app)
          // so the header stays visible while scrolling through many rows -
          // more reliable than the page-level `sticky` prop this replaced,
          // whose CSS position:sticky was silently defeated by
          // .calendar-container's overflow-y: hidden further up the tree.
          scroll={{ x: true, y: CALENDAR_SCROLL_Y }}
          size="small"
        />
      </div>
    </div>
  );
};

export default AnnualLeaveTable;
